import { computePosition, flip, offset, shift } from '@floating-ui/dom';
import { Editor } from '@tiptap/core';
import { Range, ReactRenderer, posToDOMRect } from '@tiptap/react';
import { SuggestionOptions } from '@tiptap/suggestion';
import MentionList, {
  MentionDataUser,
  MentionListProps,
  MentionListRef,
} from '../components/MentionList';

let getUsersFromStore: MentionDataUser[] = [];
export const setMentionUsersGetter = (users: MentionDataUser[]) => {
  getUsersFromStore = users;
};

let onSelectAICallback: (() => void) | null = null;
export const setOnSelectAICallback = (callback: (() => void) | null) => {
  onSelectAICallback = callback;
};

const updatePosition = (editor: Editor, element: HTMLElement) => {
  const virtualElement = {
    getBoundingClientRect: () =>
      posToDOMRect(
        editor.view,
        editor.state.selection.from,
        editor.state.selection.to
      ),
  };

  computePosition(virtualElement as any, element, {
    placement: 'bottom-start',
    strategy: 'absolute',
    middleware: [offset(8), shift(), flip()],
  }).then(({ x, y, strategy }) => {
    element.style.width = 'max-content';
    element.style.position = strategy;
    element.style.left = `${x}px`;
    element.style.top = `${y}px`;
  });
};

const suggestion: Omit<SuggestionOptions, 'editor'> = {
  items: ({ query }) => {
    const users: MentionDataUser[] = getUsersFromStore;

    if (!users) return [];

    const filteredUsers = users.filter((item) =>
      item.label.toLowerCase().startsWith(query.toLowerCase())
    );

    // Separate AI and non-AI users
    const aiUsers = filteredUsers.filter((user) => user.isAI);
    const regularUsers = filteredUsers.filter((user) => !user.isAI);

    // Take up to 5 from ai
    const selectedAI = aiUsers.slice(0, 6);
    const selectedUsers = regularUsers.slice(0, 3);

    // Combine AI users first, then regular users
    const result = [...selectedAI, ...selectedUsers];

    console.log('result', result);
    return result;
  },
  render: () => {
    let component: ReactRenderer<MentionListRef, MentionListProps> | null =
      null;
    let wrapper: HTMLDivElement | null = null;
    let container: HTMLElement | null = null;

    return {
      onStart: (props) => {
        component = new ReactRenderer(MentionList, {
          props: {
            ...props,
            onSelectAI: onSelectAICallback,
          },
          editor: props.editor,
        });

        if (!props.clientRect) {
          return;
        }

        // Find the best container - try shadow DOM first, then fallback to body
        const shadowContainer = document
          .getElementById('root-atarim')
          ?.shadowRoot?.getElementById('app-wrapper');
        container = (shadowContainer || document.body) as HTMLElement;

        // Create a wrapper div to ensure proper positioning
        wrapper = document.createElement('div');
        wrapper.style.position = 'absolute';
        wrapper.style.zIndex = '999999';
        wrapper.style.pointerEvents = 'auto';
        wrapper.style.minWidth = '250px';
        wrapper.style.maxWidth = '600px';
        wrapper.appendChild(component.element);

        // Append to container and position using Floating UI
        container.appendChild(wrapper);
        updatePosition(props.editor, wrapper);
      },

      onUpdate: (props) => {
        if (!component || !wrapper) return;
        component.updateProps({
          ...props,
          onSelectAI: onSelectAICallback,
        });

        if (!props.clientRect) {
          return;
        }

        updatePosition(props.editor, wrapper);
      },

      onKeyDown: (props: { event: KeyboardEvent }) => {
        if (!component) return false;

        if (props.event.key === 'Escape') {
          // Cleanup
          if (component) {
            component.destroy();
            component = null;
          }
          if (wrapper && wrapper.parentNode) {
            wrapper.parentNode.removeChild(wrapper);
            wrapper = null;
          }
          return true;
        }

        return component.ref?.onKeyDown(props) || false;
      },

      onExit: () => {
        if (component) component.destroy();
        component = null;
        if (wrapper && wrapper.parentNode)
          wrapper.parentNode.removeChild(wrapper);
        wrapper = null;
      },
    };
  },

  command: ({
    editor,
    range,
    props,
  }: {
    editor: Editor;
    range: Range;
    props: MentionDataUser;
  }) => {
    const nodeAfter = editor.view.state.selection.$to.nodeAfter;
    const overrideSpace = nodeAfter?.text?.startsWith(' ');

    if (overrideSpace) {
      range.to += 1;
    }

    // Removed the console.log statement as it is unnecessary in production code.

    editor
      .chain()
      .focus()
      .insertContentAt(range, [
        {
          type: props.node,
          attrs: { ...props },
        },
        {
          type: 'text',
          text: ' ',
        },
      ])
      .run();

    window.getSelection()?.collapseToEnd();
  },
};

export default suggestion;
